/**
 * Popover plumbing shared by DateTimePicker / DateRangePicker: position the
 * panel under its trigger (flipping up when it would overflow the viewport,
 * clamped horizontally), and close on outside-click / Esc. The panel is
 * portalled by the caller so it escapes overflow clipping.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const GAP = 6;
const MARGIN = 8;

export function useAnchoredPopover(
  open: boolean,
  close: () => void,
  width: number,
  /** Changes to this re-measure the position (e.g. the visible month). */
  reposKey: unknown,
) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const popH = popRef.current?.offsetHeight ?? 320;
    const below = r.bottom + GAP;
    const flipUp =
      below + popH > window.innerHeight - MARGIN && r.top - GAP - popH > MARGIN;
    let left = Math.min(r.left, window.innerWidth - width - MARGIN);
    left = Math.max(MARGIN, left);
    setCoords({ top: flipUp ? r.top - GAP - popH : below, left });
  }, [open, width, reposKey]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        popRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return { triggerRef, popRef, coords };
}
